import React, { useEffect, useRef, useState } from 'react';
import { Anchor, Box, Button, Group, Loader, Select, Text } from '@mantine/core';
import {
  IconCode,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
  IconRadar2,
  IconExternalLink,
  IconLifebuoy,
  IconClockHour4,
  IconShoppingCart,
  IconRefresh,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  StepProps,
  DOMAIN_TYPES,
  DOMAIN_TYPE_HINTS,
  platformToFramework,
} from '../types';
import { SMART_CODE_GUIDE as DOMAIN_TYPE_GUIDE } from '../../../components/GeneralComponent/smartCodeGuides';
import {
  useUniversalSmartCode,
  UniversalSmartCodeContent,
} from '../../../components/GeneralComponent/UniversalSmartCodeContent';
import SettingsApi from '../../../api/SettingsApi';
import { useOnboardingStyles } from '../onboardingStyles';
import { useOnbColors } from '../onboardingColors';
import { StepVideo, notify, startShopifyConnect } from '../common';
import pageStyles from '../Onboarding.module.css';

const VERIFY_STEPS = ['Looking for VisiSmart Script', 'Connecting to Website', 'Verifying Installation'];
const platformLabel = (v: string) => DOMAIN_TYPES.find((d) => d.value === v)?.label || v;

type DetectState = 'idle' | 'scanning' | 'done';
type VerifyState = 'idle' | 'running' | 'success' | 'failed';
type ShopifyState = 'idle' | 'checking' | 'connected' | 'disconnected';

export function Step2Script({ data, update, errors, goNext }: StepProps) {
  const { classes } = useOnboardingStyles();
  const col = useOnbColors();
  const type = data.domain.type;
  const domain = data.domain.url.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '') || 'your site';

  // A saved platform (resume) counts as a finished detection so the confirmed bar renders
  const [detect, setDetect] = useState<DetectState>(data.domain.type ? 'done' : 'scanning');
  const [detected, setDetected] = useState('');
  const [manual, setManual] = useState(false);
  const [detectError, setDetectError] = useState('');

  // Framework-aware install instructions (GHL css/footer, Shopify app, WP plugin, Next.js, …)
  const { data: smartCodeData, loading: smartCodeLoading, reload: reloadSmartCode } = useUniversalSmartCode({
    websiteId: data.domain.website_id,
    enabled: Boolean(data.domain.website_id),
  });
  const [verify, setVerify] = useState<VerifyState>(data.scriptVerified ? 'success' : 'idle');
  const [checkIdx, setCheckIdx] = useState(0);
  const timers = useRef<number[]>([]);
  const mounted = useRef(true);
  const isShopify = type === 'shopify';
  const [shopify, setShopify] = useState<ShopifyState>('idle');
  const [shopifyShop, setShopifyShop] = useState('');
  const [shopifyBusy, setShopifyBusy] = useState(false);
  // Install opened in a new tab — show the "return & refresh" panel until connected
  const [installInitiated, setInstallInitiated] = useState(false);
  // A manual Refresh returned "still not connected" — show the friendly retry message
  const [refreshNotConnected, setRefreshNotConnected] = useState(false);
  // Button spinner for the Refresh Connection action (keeps the panel visible, unlike 'checking')
  const [refreshing, setRefreshing] = useState(false);

  // Clear pending timers / guard async state on unmount
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      timers.current.forEach((t) => window.clearTimeout(t));
    };
  }, []);
  const schedule = (fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  };

  const resetVerify = () => {
    setVerify('idle');
    setCheckIdx(0);
  };

  // Platform changes invalidate any previous verification
  const changePlatform = (v: string | null) => {
    update({
      domain: { ...data.domain, type: v || '' },
      scriptVerified: false,
      scriptBypassed: false,
      shopifyConnected: false,
    });
    if (v !== 'shopify') setShopify('idle');
    if (v) {
      setDetectError('');
      persistPlatform(v);
    }
    resetVerify();
  };

  // [2a] Website scan → detect the platform via the backend (Rendertron + fingerprint)
  const scanWebsite = () => {
    setDetect('scanning');
    setDetectError('');
    SettingsApi.detectplatform({ website_address: data.domain.url })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        if (res && res.status === 1 && res.data && res.data.platform) {
          setDetected(res.data.platform);
          setDetect('done');
        } else {
          // Not reachable / couldn't determine → fall to manual selection
          setDetect('idle');
          setManual(true);
          const unreachable = res && res.data && res.data.reachable === false;
          const message = res && res.message ? res.message : 'Please choose your platform from the list.';
          if (unreachable) {
            setDetectError(`We couldn't reach ${domain}. Please check the domain, or select your platform below.`);
            notify('error', message, "We couldn't reach your website");
          } else {
            setDetectError("We couldn't detect your website platform. Please select it below.");
            notify('warning', message, "Couldn't auto-detect your platform");
          }
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        setDetect('idle');
        setManual(true);
        setDetectError("We couldn't detect your website platform. Please select it below.");
        notify('warning', 'Please choose your platform from the list.', "Couldn't auto-detect your platform");
      });
  };

  const confirmDetected = () => {
    changePlatform(detected);
    setManual(false);
  };

  // Scan was wrong → reveal the manual platform dropdown
  const chooseManually = () => setManual(true);

  // [2e] Animated verification driven by the real backend check.
  const runVerify = () => {
    // Clicking "Check Again" mid-run must not leave the previous animation ticking
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    setVerify('running');
    setCheckIdx(0);

    // Kick off the real check immediately; the checklist animates while it runs
    const check = SettingsApi.verifysmartcode({ website_id: data.domain.website_id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => Boolean(res && res.status === 1 && res.data && res.data.detected))
      .catch(() => false);

    // Advance the checklist only up to the last step, which keeps spinning
    // until the real API resolves (renders can take 10-30s).
    let i = 0;
    const tick = () => {
      if (i < VERIFY_STEPS.length - 1) {
        i += 1;
        setCheckIdx(i);
        schedule(tick, 780);
      }
    };
    schedule(tick, 600);

    // Final state comes from the API, independent of the animation timing
    check.then((found) => {
      if (!mounted.current) return;
      setCheckIdx(VERIFY_STEPS.length - 1);
      if (found) {
        setVerify('success');
        update({ scriptVerified: true, scriptBypassed: false });
      } else {
        setVerify('failed');
      }
    });
  };

  // [2g] Let dynamic / cached sites continue without blocking — advance to the next step.
  // The bypass is persisted so a resumed onboarding does not force a re-verify.
  const continueAnyway = () => {
    update({ scriptBypassed: true });
    if (data.domain.website_id) {
      SettingsApi.savesmartcodebypass({ website_id: data.domain.website_id, bypassed: 1 }).catch(() => {});
    }
    goNext?.();
  };

  // [2] Auto-start detection on load — the domain was entered on step 1
  useEffect(() => {
    if (!data.domain.type && !manual) scanWebsite();
    // eslint-disable-next-line
  }, []);

  // Save the chosen platform on the website record so the correct
  // install instructions (GHL / Shopify / WordPress / SPA) come back.
  const persistPlatform = (platformValue: string) => {
    if (!data.domain.website_id || !platformValue) return;
    const { front_end_framework, framework_type } = platformToFramework(platformValue);
    SettingsApi.addeditdomain({
      site_type: 0,
      site_name: data.domain.name,
      website_address: data.domain.url,
      website_id: data.domain.website_id,
      front_end_framework,
      framework_type,
    })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        if (res && res.status === 1) {
          reloadSmartCode();
        } else {
          // Silently failing here would show install instructions for the wrong platform
          notify('error', (res && res.message) || 'Could not save your platform. Please try again.', 'Platform not saved');
        }
      })
      .catch(() => {
        if (mounted.current) notify('error', 'Could not save your platform. Please try again.', 'Platform not saved');
      });
  };

  const guideUrl = (type && DOMAIN_TYPE_GUIDE[type]) || DOMAIN_TYPE_GUIDE.custom;

  // Shopify installs the Smart Code through the Public App, so the OAuth
  // connection — not a manual paste — is what gates this step.
  // mode: 'mount' shows the loader, 'manual' (Refresh button) shows the loader + the not-connected
  // message on failure, 'silent' (tab-focus auto-check) only promotes to connected without flicker.
  const verifyShopify = (mode: 'mount' | 'manual' | 'silent' = 'manual') => {
    if (!data.domain.website_id) return;
    if (mode === 'mount') setShopify('checking');
    if (mode === 'manual') { setRefreshing(true); setRefreshNotConnected(false); }
    SettingsApi.getshopifyoauthstatus({ system_website_id: data.domain.website_id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        if (mode === 'manual') setRefreshing(false);
        const connected = Boolean(res && res.status === 1 && res.data && res.data.connected);
        if (connected) {
          setShopify('connected');
          setShopifyShop(res.data.shop_domain || '');
          setRefreshNotConnected(false);
          update({ shopifyConnected: true });
        } else {
          if (mode === 'mount') setShopify('disconnected');
          update({ shopifyConnected: false });
          if (mode === 'manual') setRefreshNotConnected(true);
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        if (mode === 'manual') setRefreshing(false);
        if (mode === 'mount') setShopify('disconnected');
        update({ shopifyConnected: false });
        if (mode === 'manual') setRefreshNotConnected(true);
      });
  };

  // Re-check the connection whenever Shopify becomes the active platform.
  useEffect(() => {
    if (isShopify && data.domain.website_id) verifyShopify('mount');
    // eslint-disable-next-line
  }, [isShopify, data.domain.website_id]);

  // After the install tab is opened, silently re-verify when the user returns to this tab,
  // so a completed install flips to "Connected" without them having to click Refresh.
  useEffect(() => {
    if (!isShopify || !installInitiated || shopify === 'connected') return;
    const onFocus = () => verifyShopify('silent');
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line
  }, [isShopify, installInitiated, shopify, data.domain.website_id]);

  const connectShopify = () => {
    const opened = startShopifyConnect(data.domain.website_id, 'main', `/onboarding${window.location.search}`);
    if (opened) {
      setInstallInitiated(true);
      setRefreshNotConnected(false);
    }
  };

  const disconnectShopify = () => {
    setShopifyBusy(true);
    SettingsApi.disconnectshopify({ system_website_id: data.domain.website_id })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!mounted.current) return;
        setShopifyBusy(false);
        if (res && res.status === 1) {
          setShopify('disconnected');
          setShopifyShop('');
          setInstallInitiated(false);
          setRefreshNotConnected(false);
          resetVerify();
          update({ shopifyConnected: false, scriptVerified: false });
          notify('success', 'Shopify store disconnected.', 'Disconnected');
        } else {
          notify('error', (res && res.message) || 'Could not disconnect the store.', 'Error');
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        setShopifyBusy(false);
        notify('error', 'Could not disconnect the store.', 'Error');
      });
  };

  return (
    <Box className={`${classes.inner} ${pageStyles.stepEnter}`}>
      <div className={classes.mark}>
        <IconCode size={28} />
      </div>
      <h1 className={classes.title}>Choose your website platform</h1>
      <p className={classes.subtitle}>
        Select the platform your website uses so we can provide the correct installation instructions.
      </p>

      <StepVideo
        sublabel="How to install your Smart Code"
        modalTitle="How to install your tracking script"
        videoUrl="https://vimeo.com/1158866937"
      />

      <div className={classes.fields}>
        {/* [2] Auto-detecting on load */}
        {!manual && detect === 'scanning' && (
          <div className={classes.detectCard}>
            <Loader size="sm" color="grape" />
            <Box style={{ flex: 1 }}>
              <Text fw={700} fz="sm" c={col.textPrimary}>
                Detecting your website…
              </Text>
              <Text fz={13} c="dimmed">
                ⏳ Scanning {domain} — this usually takes a few seconds…
              </Text>
            </Box>
          </div>
        )}

        {/* [3a] Scan result — confirm correct or choose another platform */}
        {!manual && detect === 'done' && !type && (
          <div
            className={classes.detectCard}
            style={{ borderColor: col.greenBorder, background: col.greenBg, flexDirection: 'column', alignItems: 'stretch', gap: 12 }}
          >
            <Group spacing={12} noWrap align="flex-start">
              <div className={classes.detectIcon} style={{ background: col.greenBg, color: col.green }}>
                <IconCircleCheck size={20} />
              </div>
              <Box style={{ flex: 1 }}>
                <Text fw={700} fz="sm" c={col.greenDeep}>
                  We detected your website is built with {platformLabel(detected)}.
                </Text>
                <Text fz={13} c={col.greenText}>
                  Is that correct?
                </Text>
              </Box>
            </Group>
            <Group spacing={8} position="right">
              <Button radius="md" color="teal" leftIcon={<IconCheck size={16} />} onClick={confirmDetected}>
                Yes, That's Correct
              </Button>
              <Button radius="md" variant="default" onClick={chooseManually}>
                Choose Another Platform
              </Button>
            </Group>
          </div>
        )}

        {/* Confirmed detected platform — compact bar with a change option */}
        {!manual && type && detect === 'done' && (
          <div className={classes.detectCard} style={{ borderColor: col.greenBorder, background: col.greenBg }}>
            <div className={classes.detectIcon} style={{ background: col.greenBg, color: col.green }}>
              <IconCircleCheck size={20} />
            </div>
            <Box style={{ flex: 1 }}>
              <Text fw={700} fz="sm" c={col.greenDeep}>
                Platform: {platformLabel(type)}
              </Text>
              <Text fz={13} c={col.greenText}>
                Detected automatically.
              </Text>
            </Box>
            <Button radius="md" variant="default" onClick={chooseManually}>
              Change
            </Button>
          </div>
        )}

        {/* [2] Manual platform dropdown — only shown when the scan was wrong */}
        {manual && (
          <Box style={{ width: '100%' }}>
            {detectError && (
              <div className={classes.cachingBox} style={{ marginBottom: 10 }}>
                <Text fz={13.5} c={col.amberText}>
                  {detectError}
                </Text>
              </div>
            )}
            <Select
              size="md"
              radius="md"
              searchable
              label="Website platform"
              classNames={{ label: classes.fieldLabel }}
              placeholder="Choose your website platform"
              data={DOMAIN_TYPES}
              value={type || null}
              error={errors.type}
              onChange={changePlatform}
            />
          </Box>
        )}

        {type && (
          <Box className={pageStyles.fadeIn} style={{ width: '100%', display: 'grid', gap: 14 }}>
            <div className={classes.hintBox}>{DOMAIN_TYPE_HINTS[type]}</div>

            {/* Platform-specific install guide */}
            <Group position="right" align="center" noWrap style={{ width: '100%' }}>
              <Anchor href={guideUrl} target="_blank" rel="noreferrer" fz={13.5} fw={600} c={col.purple}>
                <Group spacing={6} noWrap>
                  <IconExternalLink size={15} /> How to install on {platformLabel(type)}
                </Group>
              </Anchor>
            </Group>

            {/* Shopify: the Public App installs the Smart Code — no manual paste */}
            {isShopify ? (
              <Box style={{ width: '100%', textAlign: 'left' }}>
                {shopify === 'checking' && (
                  <Group position="center" py={20}>
                    <Loader size="sm" color="grape" />
                  </Group>
                )}

                {shopify === 'connected' && (
                  <div className={classes.detectCard} style={{ borderColor: col.greenBorder, background: col.greenBg }}>
                    <div className={classes.detectIcon} style={{ background: col.greenBg, color: col.green }}>
                      <IconCircleCheck size={20} />
                    </div>
                    <Box style={{ flex: 1 }}>
                      <Text fw={700} fz="sm" c={col.greenDeep}>
                        Shopify store connected
                      </Text>
                      <Text fz={13} c={col.greenText}>
                        {shopifyShop || 'Your store'} — the Visiopt app installs the Smart Code for you.
                      </Text>
                    </Box>
                    <Group spacing={6} noWrap>
                      <Button radius="md" variant="default" disabled={shopifyBusy} onClick={connectShopify}>
                        Reconnect
                      </Button>
                      <Button radius="md" variant="subtle" color="red" loading={shopifyBusy} onClick={disconnectShopify}>
                        Disconnect
                      </Button>
                    </Group>
                  </div>
                )}

                {/* Not started yet: instructions + Connect button (opens Shopify in a new tab) */}
                {shopify === 'disconnected' && !installInitiated && (
                  <>
                    <div className={classes.stepNote}>
                      <div className={classes.noteHeading}>Connect your Shopify store</div>
                      <ol style={{ margin: '4px 0 0', paddingLeft: 20, color: col.textSecondary, fontSize: 13.5, lineHeight: 1.7 }}>
                        <li>Click <b>Connect Shopify Store</b> — Shopify opens in a <b>new tab</b> to install the Visiopt app.</li>
                        <li>Approve the install for the store you want to track.</li>
                        <li>In the theme editor, turn ON the <b>Visiopt Integration</b> toggle and click <b>Save</b>.</li>
                        <li>Return to this tab and click <b>Refresh Connection</b> to verify.</li>
                      </ol>
                    </div>
                    <Button
                      className={classes.blackBtn}
                      h={48}
                      radius="md"
                      fullWidth
                      mt={12}
                      leftIcon={<IconShoppingCart size={18} />}
                      onClick={connectShopify}
                    >
                      Connect Shopify Store
                    </Button>
                  </>
                )}

                {/* Install opened in a new tab: wait for the user to finish, then Refresh Connection */}
                {shopify === 'disconnected' && installInitiated && (
                  <>
                    <div className={classes.stepNote} style={{ borderColor: col.amberBorder, background: col.amberBg }}>
                      <Group spacing={9} noWrap align="flex-start">
                        <IconExternalLink size={18} color={col.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                        <Box>
                          <div className={classes.noteHeading}>Complete the Shopify installation in the new tab</div>
                          <Text fz={13.5} c={col.textSecondary} mt={4} style={{ lineHeight: 1.7 }}>
                            We opened Shopify in a new browser tab. Approve the Visiopt app install there, then return to this tab and click <b>Refresh Connection</b> to verify your store connection.
                          </Text>
                        </Box>
                      </Group>
                    </div>

                    {refreshNotConnected && (
                      <div className={classes.stepNote} style={{ borderColor: col.amberBorder2, background: col.amberBg2, marginTop: 10 }}>
                        <Group spacing={8} noWrap align="flex-start">
                          <IconAlertTriangle size={16} color={col.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                          <Text fz={13} c={col.amberText} style={{ lineHeight: 1.6 }}>
                            <b>Shopify is not connected yet.</b> Please complete the app installation in the other tab, then return here and click <b>Refresh Connection</b> again.
                          </Text>
                        </Group>
                      </div>
                    )}

                    <Group mt={12} spacing={10} noWrap grow>
                      <Button
                        className={classes.blackBtn}
                        h={48}
                        radius="md"
                        leftIcon={<IconRefresh size={18} />}
                        loading={refreshing}
                        onClick={() => verifyShopify('manual')}
                      >
                        Refresh Connection
                      </Button>
                      <Button
                        variant="default"
                        h={48}
                        radius="md"
                        leftIcon={<IconShoppingCart size={16} />}
                        onClick={connectShopify}
                      >
                        Reopen Shopify
                      </Button>
                    </Group>
                  </>
                )}
              </Box>
            ) : (
              <>
                {/* Framework-aware instructions: GHL css/footer, WP plugin, SPA, Next.js */}
                <Box style={{ width: '100%', textAlign: 'left' }}>
                  {smartCodeLoading ? (
                    <Group position="center" py={20}>
                      <Loader size="sm" color="grape" />
                    </Group>
                  ) : (
                    <UniversalSmartCodeContent
                      data={smartCodeData}
                    />
                  )}
                </Box>

                {/* [3b] Next step — numbered instructions */}
                <div className={classes.stepNote}>
                  <div className={classes.noteHeading}>Next Step</div>
                  <ol style={{ margin: '4px 0 0', paddingLeft: 20, color: col.textSecondary, fontSize: 13.5, lineHeight: 1.7 }}>
                    <li>Copy the Smart Code above.</li>
                    <li>Add it to your website following the instructions.</li>
                    <li>Return here when you're finished.</li>
                    <li>
                      Click <b>I've Installed the Smart Code</b>.
                    </li>
                  </ol>
                </div>
              </>
            )}

            {/* [2e] Verification experience */}
            {verify === 'running' && (
              <div className={classes.verifyList}>
                {VERIFY_STEPS.map((label, i) => {
                  const done = i < checkIdx;
                  const activeItem = i === checkIdx;
                  return (
                    <div
                      key={label}
                      className={classes.verifyItem}
                      style={{ color: done ? col.green : activeItem ? col.textPrimary : col.textMuted }}
                    >
                      {done ? (
                        <IconCircleCheck size={18} />
                      ) : activeItem ? (
                        <Loader size={16} color="grape" />
                      ) : (
                        <span className={classes.verifyPending} />
                      )}
                      {label}
                    </div>
                  );
                })}
              </div>
            )}

            {verify === 'success' && (
              <Group className={classes.verifyOk} position="center" spacing={10} noWrap>
                <IconCircleCheck size={22} color={col.green} />
                <Box style={{ textAlign: 'left', flex: 1 }}>
                  <Text fw={700} fz="sm" c={col.greenDeep}>
                    Success! Tracking script detected
                  </Text>
                  <Text fz={12.5} c={col.greenText}>
                    VisiSmart is live on {domain}. You're good to continue.
                  </Text>
                </Box>
                <Button size="xs" radius="md" variant="subtle" color="gray" onClick={runVerify}>
                  Re-check
                </Button>
              </Group>
            )}

            {/* [3c] Failure — single action-oriented box + Good to Know */}
            {verify === 'failed' && !data.scriptBypassed && (
              <>
                <div className={classes.failBox}>
                  <div className={classes.noteHeading} style={{ color: col.red }}>
                    <IconCircleX size={16} /> Smart Code Not Detected Yet
                  </div>
                  <Text fz={13.5} c={col.redDeep} mb={4}>
                    We couldn't detect your VisiSmart Smart Code yet. This is usually resolved by
                    completing the following steps:
                  </Text>
                  {[
                    'Make sure your changes have been published.',
                    'Refresh your website.',
                    'If your website uses caching or a CDN, such as Cloudflare, LiteSpeed, WP Rocket, etc., clear the cache.',
                    'Wait 2–5 minutes for the changes to propagate.',
                  ].map((r) => (
                    <div key={r} className={classes.bulletRow} style={{ color: col.redDeep }}>
                      <IconCheck size={15} color={col.red} style={{ marginTop: 2, flexShrink: 0 }} />
                      <span>{r}</span>
                    </div>
                  ))}
                  <Text fz={13.5} c={col.redDeep} mt={8}>
                    Then click <b>Check Again</b>.
                  </Text>
                  <Group spacing={18} mt={12} align="center">
                    <Text fz={13.5} fw={600} c={col.redDeep}>
                      Still need help?
                    </Text>
                    <Anchor href={guideUrl} target="_blank" rel="noreferrer" className={classes.linkBtn}>
                      <IconExternalLink size={15} /> View Troubleshooting Guide
                    </Anchor>
                    <Anchor href="mailto:support@visiopt.com" className={classes.linkBtn}>
                      <IconLifebuoy size={15} /> Contact Support
                    </Anchor>
                  </Group>
                </div>

                <Group grow>
                  <Button className={classes.blackBtn} h={48} radius="md" leftIcon={<IconRadar2 size={18} />} onClick={runVerify}>
                    Check Again
                  </Button>
                  {/* Shopify tracks only via the app, so bypassing would silently break it */}
                  {!isShopify && (
                    <Button variant="default" h={48} radius="md" onClick={continueAnyway}>
                      Continue Anyway
                    </Button>
                  )}
                </Group>

                <div className={classes.bypassBox}>
                  <div className={classes.noteHeading} style={{ color: col.purpleDeep }}>
                    <IconClockHour4 size={15} /> Good to Know
                  </div>
                  <Text fz={13} c={col.purpleDeep}>
                    If your website uses a dynamic setup where automatic detection isn't possible, you can safely ignore this
                    message and click <b>Continue Anyway</b>. Otherwise, please make sure the smartcode is
                    installed correctly, as tracking may not work until it is detected.
                  </Text>
                </div>
              </>
            )}

            {/* [2g] Bypassed state */}
            {data.scriptBypassed && (
              <div className={classes.bypassBox} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <IconClockHour4 size={22} color={col.purple} style={{ flexShrink: 0 }} />
                <Box style={{ flex: 1 }}>
                  <Text fw={700} fz="sm" c={col.purpleDeep}>
                    Verification paused — you can continue
                  </Text>
                  <Text fz={12.5} c={col.purple}>
                    We'll keep checking {domain} in the background for the next 15 minutes.
                  </Text>
                </Box>
                <Button size="xs" radius="md" variant="subtle" color="grape" onClick={() => { update({ scriptBypassed: false }); runVerify(); }}>
                  Verify now
                </Button>
              </div>
            )}

            {/* Idle → launch verification. For Shopify only after the store is connected. */}
            {verify === 'idle' && !data.scriptBypassed && (!isShopify || shopify === 'connected') && (
              <Button
                className={classes.blackBtn}
                h={48}
                radius="md"
                fullWidth
                leftIcon={<IconCircleCheck size={18} />}
                onClick={runVerify}
              >
                {isShopify ? 'Verify Tracking' : "I've Installed the Smart Code"}
              </Button>
            )}
          </Box>
        )}
      </div>
    </Box>
  );
}
